// ProductDetail v2 (SGC new layout since 2025)
import { PromotionGroupType, SelectionPromotion } from '@teko/cart/dist/v2';
import {
  Breadcrumbs,
  Card,
  Container,
  Row,
  Shape,
  styled,
} from '@teko/ds-desktop';
import { NoProductsFound } from '@teko/teko-ecommerce';
import { trackViewProductDetail } from '@teko/tracking';
import { FAQSection } from 'components/FAQ';
import { InlineBanner } from 'components/InlineBanner';
import {
  NotFoundProductLayout,
  Wrapper,
} from 'components/ProductDetail/styles';
import { WishlistButton } from 'components/WishlistButton';
import { RecentViewedProducts } from 'components/desktop/organisms';
import { RECENTLY_VIEWED_PRODUCTS_KEY } from 'constants/index';
import { INLINE_BANNER_CLASSNAME } from 'constants/smartBanner';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { saveRecentlyProduct } from 'helpers';
import { useIntersectionObserver } from 'hooks';
import { t } from 'i18n';
import { BreadcrumbSchema } from 'modules/product-detail/shared/BreadcrumbSchema';
import { DefaultPromotions } from 'modules/product-detail/shared/DefaultPromotions';
import { FlashSalePromotionBlock } from 'modules/product-detail/shared/FlashSalePromotionBlock';
import { PaymentPromotion } from 'modules/product-detail/shared/PaymentPromotion';
import { Policies } from 'modules/product-detail/shared/Policies';
import { ProductInfo } from 'modules/product-detail/shared/ProductInfo';
import { ProductNews } from 'modules/product-detail/shared/ProductNews';
import { ProductProviderInfo } from 'modules/product-detail/shared/ProductProviderInfo';
import { ReviewSection as NewReviewSection } from 'modules/product-detail/shared/ProductReview/ReviewSection';
import { ReviewSection } from 'modules/product-detail/shared/ReviewSection';
import { TierPromotions } from 'modules/product-detail/shared/TierPromotion';
import dynamic from 'next/dynamic';
import React, { RefObject, useEffect, useMemo } from 'react';
import { Box, Flex } from 'reflexbox';
import { MetaData } from '../../shared/MetaData';
import { ProductUnitAndPrice } from '../../shared/ProductUnitAndPrice';
import { ShareButtons } from '../DetailPage/ShareButtons';
import { ActionButtons } from './ActionButtons';
import { BottomButtons } from './BottomButtons';
import { CustomRecentProductCarousel } from './CustomRecentProductCarousel';
import { ProductImages } from './ProductImages';
import { ProductRecipes } from './ProductRecipes';
import { QuantityBlock } from './QuantityBlock';
import { RelatedPromotions } from './RelatedPromotions';
import { ComboCarousel } from './combo/ComboCarousel';

const ConfirmOver18YearOldModal = dynamic(() =>
  import('../../shared/ConfirmOver18YearOldModal').then(
    (mod) => mod.ConfirmOver18YearOldModal,
  ),
);

const RelatedProducts = dynamic(() =>
  import('../../shared/SGCRelatedProducts').then((mod) => mod.RelatedProducts),
);

const SGCFrequentlyBoughtTogether = dynamic(() =>
  import('../../shared').then((mod) => mod.SGCFrequentlyBoughtTogether),
);

const NewProductDescription = dynamic(() =>
  import('../DetailPage/NewProductDescription').then(
    (mod) => mod.NewProductDescription,
  ),
);

interface Props {}

export const MAX_WIDTH = 1600;
const MAX_LINES_BEFORE_ELLIPSIS = 2;

export const DetailPageSGC = ({}: Props) => {
  const { epicOmni1428, epicOmni1585, epicOmni1550 } = useTenantConfig();
  const {
    breadcrumbs,
    product,
    promotionBlocks,
    showPopupCheckAge,
    setShowPopupCheckAge,
    inlineBanners,
    relatedProducts,
  } = useProductDetailState();
  const [actionButtonsRef, isVisibleActionButtons] = useIntersectionObserver();
  const [triggerFooterRef, isVisibleTriggerFooter] = useIntersectionObserver();

  const showTierPromotionsBlock = !!epicOmni1428 || !!epicOmni1585;

  const isPromotionDefaultGroupType = (
    promotions: SelectionPromotion[],
    groupType?: PromotionGroupType,
  ) => {
    return groupType
      ? groupType === PromotionGroupType.GROUP_TYPE_DEFAULT
      : promotions.every((p) => !p.isSelectable);
  };

  const newPromotionBlocks = useMemo(() => {
    if (!showTierPromotionsBlock) return promotionBlocks;
    // create a promotion with groupType of GROUP_TYPE_TIER_PROMOTIONS
    const tierPromotionBlock = {
      title: '',
      groupType: PromotionGroupType.GROUP_TYPE_TIER_PROMOTIONS,
      promotions: promotionBlocks.flatMap((p) => p.promotions),
      description: '',
    };

    /**  because cart return promotions with groupType in order of Default --> Selectable --> Exclusive
     * we only need to check if default type available or not
     * if yes --> add tier promotions type after default type
     * if no --> add tier promotions to top of the list
     * we also check if there isn't any promotion in the list we return an empty array instead
     */
    return !!promotionBlocks.length
      ? isPromotionDefaultGroupType(
          promotionBlocks[0].promotions,
          promotionBlocks[0].groupType,
        )
        ? [
            promotionBlocks[0],
            tierPromotionBlock,
            ...promotionBlocks.slice(1, promotionBlocks.length),
          ]
        : [tierPromotionBlock, ...promotionBlocks]
      : [];
  }, [promotionBlocks, showTierPromotionsBlock]);

  const mapTierPromotions = (promotions: SelectionPromotion[]) =>
    promotions.map((promotion) => ({
      cartPromotionId: null,
      promotionId: promotion.promotionId,
      promotionCode: promotion.promotionCode,
      isSelectable: promotion.isSelectable,
      imageUrl: promotion.imageUrl,
      title: promotion.title,
      description: promotion.description,
      endedAt: promotion.endedAt?.toString() || '',
      name: promotion.name || '',
      tierDescriptions: promotion.tierDescriptions,
      highlightedDescriptions: promotion.highlightedDescriptions,
      customerTierDescriptions: promotion.customerTierDescriptions,
    }));

  useEffect(() => {
    if (product) {
      saveRecentlyProduct(product.sku, RECENTLY_VIEWED_PRODUCTS_KEY);
      trackViewProductDetail({
        price: product.price?.price,
        name: product.name,
        sku: product.sku,
        brand: product.brand?.name,
        category: product.categories?.[0]?.name || '',
        sellerSku: product?.sellerSku,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.sku]);

  if (!product) {
    return (
      <NotFoundProductLayout>
        <NoProductsFound show text={t('Không tìm thấy sản phẩm')} />
      </NotFoundProductLayout>
    );
  }

  return (
    <>
      <BreadcrumbSchema />
      <MetaData />
      <Container fluid maxWidth={MAX_WIDTH}>
        <Wrapper>
          <Breadcrumbs breadcrumbs={breadcrumbs || []} />
          <DetailBlock
            marginBottom={24}
            backgroundColor="white"
            padding={2} //8px
            style={{ borderRadius: '1rem' }}
          >
            <Box width="36.36%" marginRight={24}>
              <Shape padding="s8">
                <ProductImages />
                <Row
                  align="bottom"
                  padding={['s0', 's8']}
                  justify="start"
                  marginBottom="s16"
                >
                  <ShareButtons />
                  <React.Fragment>
                    <Divider />
                    <span
                      style={{
                        fontSize: 13,
                        lineHeight: '20px',
                        fontWeight: 400,
                      }}
                    >
                      {t('Yêu thích')}
                    </span>
                    <WishlistButton sku={product.sku} size="24" />
                  </React.Fragment>
                </Row>
                <InlineBanner
                  inlineBanners={inlineBanners}
                  inlineBannerClass={
                    INLINE_BANNER_CLASSNAME.INLINE_PDP_RELATEDPROMO
                  }
                />
                <ProductProviderInfo />
                <Policies />
              </Shape>
            </Box>
            <Box width="61.67%">
              <Shape padding="s8">
                <FlashSalePromotionBlock />
                <ProductInfo product={product} />
                <ProductUnitAndPrice />
                {newPromotionBlocks?.map((block, index) => {
                  const isDefaultGroup = isPromotionDefaultGroupType(
                    block.promotions,
                    block.groupType,
                  );
                  return isDefaultGroup ? (
                    // If all `title` fields in the default promotions array of default group type are empty strings, we should hide this block */}
                    block.promotions.every(
                      (promotion) => !promotion.title,
                    ) ? null : (
                      <DefaultPromotions
                        promotions={block.promotions}
                        key={index}
                      />
                    )
                  ) : block.groupType ===
                    PromotionGroupType.GROUP_TYPE_TIER_PROMOTIONS ? (
                    <>
                      {epicOmni1585 && (
                        <TierPromotions
                          productPromotions={[
                            {
                              title: '',
                              groupType: undefined,
                              promotions: mapTierPromotions(block.promotions),
                            },
                          ]}
                          isCustomerTierPromotions
                        />
                      )}
                      {epicOmni1428 && (
                        <TierPromotions
                          productPromotions={[
                            {
                              title: '',
                              groupType: undefined,
                              promotions: mapTierPromotions(block.promotions),
                            },
                          ]}
                          maxLinesBeforeEllipsis={MAX_LINES_BEFORE_ELLIPSIS}
                        />
                      )}
                    </>
                  ) : null;
                })}
                <QuantityBlock />
                <ActionButtons
                  actionButtonsRef={
                    actionButtonsRef as RefObject<HTMLDivElement>
                  }
                />
                <ProductRecipes />
                <RelatedPromotions />
                <PaymentPromotion />
              </Shape>
            </Box>
          </DetailBlock>
          <ComboCarousel />
          <RelatedProducts relatedProducts={relatedProducts} />
          <SGCFrequentlyBoughtTogether />
          {epicOmni1550 ? (
            <NewReviewSection />
          ) : (
            <ReviewSection product={product} wrapperBorderRadius="16px" />
          )}

          <Flex marginBottom={16} marginTop={16} alignItems={'stretch'}>
            <Box width="68.5%" marginRight={24}>
              <NewProductDescription />
            </Box>
            <Box width="30%">
              <ProductNews />
            </Box>
          </Flex>
          {!!product.seoInfo?.faq?.length && (
            <Card marginTop="s16">
              <FAQSection
                faq={product.seoInfo.faq}
                style={{ padding: '0px' }}
              />
            </Card>
          )}
          <RecentViewedProducts
            render={(products) => (
              <CustomRecentProductCarousel recentProducts={products} />
            )}
            excludedSkus={[product.sku]}
          />
        </Wrapper>
      </Container>
      {!isVisibleActionButtons && !isVisibleTriggerFooter && <BottomButtons />}

      {showPopupCheckAge && (
        <ConfirmOver18YearOldModal
          showPopupCheckAge={showPopupCheckAge}
          setShowPopupCheckAge={setShowPopupCheckAge}
          okText={t('Tôi trên 18 tuổi')}
        />
      )}
      <div ref={triggerFooterRef as RefObject<HTMLDivElement>} />
    </>
  );
};

const DetailBlock = styled(Flex)``;

const Divider = styled.div`
  ::before {
    content: '|';
    margin: 0 24px;
    color: #0000000f;
  }
`;
